import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { ScrollText, Loader2, XCircle, ShieldCheck, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUSD = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtTs = (s) => { try { return s ? new Date(s).toUTCString() : "—"; } catch { return s || "—"; } };

const Row = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2.5 border-b border-[#2A2A22]">
    <div className="sm:w-56 shrink-0 text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A]">{label}</div>
    <div className="text-[13px] text-[#ECECE4]">{value}</div>
  </div>
);

export default function PublicFundingRegister() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/funding/register/${token}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "This funding program could not be found.");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const p = data?.program;
  const drafts = data?.drafts || [];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] px-4 py-10" data-testid="funding-register-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/funding" className="text-[12px] text-[#8A8A8A] hover:text-[#ECECE4]" data-testid="frg-back-link">← All programs</Link>
        <div className="mt-5 bg-[#13130E] border border-[#2A2A22] rounded-xl overflow-hidden">
          <div className="px-7 py-6 border-b border-[#2A2A22] flex items-center gap-3">
            <ScrollText size={22} className="text-[#B8860B]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">SEUTRADE FINANCIAL SERVICES · Draft Issuance Register</div>
              <div className="text-xl font-serif">Master Private Negotiable Instrument Funding Program</div>
            </div>
          </div>
          {error ? (
            <div className="px-7 py-12 text-center" data-testid="frg-error">
              <XCircle size={34} className="text-[#FF8585] mx-auto mb-3" />
              <div className="text-[#FF8585] text-sm">{error}</div>
            </div>
          ) : !data ? (
            <div className="px-7 py-16 text-center" data-testid="frg-loading"><Loader2 className="animate-spin inline text-[#B8860B]" /></div>
          ) : (
            <div className="px-7 py-6" data-testid="frg-result">
              {p.executed && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 text-[11px] uppercase tracking-[0.14em] font-bold bg-[#0B6B4F]/15 text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-full" data-testid="frg-executed-badge">
                  <ShieldCheck size={14} /> Executed & Notarized
                </div>
              )}
              <Row label="Program No." value={<span className="font-semibold text-[#E4C97A]">{p.program_no}</span>} />
              <Row label="Program Name" value={p.program_name} />
              <Row label="Issuer / Drawer" value={p.issuer_name} />
              <Row label="Acceptor / Drawee" value={p.acceptor_name || "—"} />
              <Row label="Revolving Limit" value={fmtUSD(p.program_limit)} />
              <Row label="Outstanding" value={fmtUSD(p.outstanding_total)} />
              <Row label="Available Headroom" value={<span className="text-[#3DBB8E]">{fmtUSD(p.available_headroom)}</span>} />
              <Row label="Drafts Issued" value={p.drafts_count || 0} />
              <Row label="Governing Law" value={p.governing_law} />
              <Row label="Collateralized" value={p.collateralized ? "Yes — Security Agreement applies" : "No"} />

              {(p.signatures || []).length > 0 && (
                <div className="mt-6" data-testid="frg-signatures">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-3">Execution — E-SIGN / UETA</div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {p.signatures.map((s, i) => (
                      <div key={i} className="bg-[#0F0F0A] border border-[#2A2A22] rounded-lg p-4">
                        <div className="text-[#E4C97A] italic" style={{ fontFamily: "cursive" }}>{s.signature}</div>
                        <div className="text-[10px] text-[#8A8A8A] mt-2">{s.role}</div>
                        <div className="text-[11px] text-[#9A9A90]">{s.party}</div>
                        <div className="text-[10px] text-[#5C5C5C] mt-1">By: {s.signatory}, {s.title}</div>
                      </div>
                    ))}
                  </div>
                  {p.notary && p.notary.notary_name && (
                    <div className="mt-4 text-[11px] text-[#9A9A90] leading-relaxed">
                      <span className="text-[#E4C97A] font-semibold">{p.notary.seal}</span> — Subscribed, sworn and notarially sealed before {p.notary.notary_name}, Notary Public, {p.notary.jurisdiction}. Commission {p.notary.commission_no}.
                    </div>
                  )}
                </div>
              )}
              {p.execution_hash && <div className="mt-4 text-[10px] font-mono text-[#5C5C5C] break-all" data-testid="frg-hash">Agreement SHA-256: {p.execution_hash} · Executed {fmtTs(p.executed_at)}</div>}

              <div className="mt-7">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-2">Issued Private Negotiable Instruments</div>
                {drafts.length === 0 ? (
                  <p className="text-[#5C5C5C] text-sm" data-testid="frg-no-drafts">No drafts have been issued under this program yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12px]" data-testid="frg-drafts-table">
                      <thead><tr className="text-[#8A8A8A] text-left border-b border-[#2A2A22]"><th className="py-2 pr-4">Draft No.</th><th className="py-2 pr-4">Payee</th><th className="py-2 pr-4">Face</th><th className="py-2 pr-4">Disc.</th><th className="py-2 pr-4">Net</th><th className="py-2 pr-4">Maturity</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4"></th></tr></thead>
                      <tbody>
                        {drafts.map((d) => (
                          <tr key={d.draft_no} className="border-b border-[#1C1C16]" data-testid={`frg-draft-${d.draft_no}`}>
                            <td className="py-2 pr-4 font-mono text-[#E4C97A]">{d.draft_no}{d.medallion_no ? <div className="text-[9px] text-[#8A8A8A] font-sans">{d.medallion_no}{d.reserve_backing && d.reserve_backing.backed ? " · " + d.reserve_backing.reserve_no : ""}</div> : null}</td>
                            <td className="py-2 pr-4">{d.payee_name}</td>
                            <td className="py-2 pr-4 text-[#3DBB8E]">{fmtUSD(d.face_amount)}</td>
                            <td className="py-2 pr-4">{d.discount_rate}%</td>
                            <td className="py-2 pr-4 text-[#9DB4E8]">{fmtUSD(d.discounted_value)}</td>
                            <td className="py-2 pr-4 text-[#9A9A90]">{d.maturity_date}</td>
                            <td className="py-2 pr-4"><span className="text-[10px] uppercase tracking-[0.1em] text-[#E4C97A]">{d.status}</span></td>
                            <td className="py-2 pr-4 whitespace-nowrap">
                              <Link to={`/funding/verify/${d.verify_token}`} data-testid={`frg-verify-${d.draft_no}`} className="inline-flex items-center gap-1 text-[10px] text-[#3DBB8E] hover:text-[#6fe0b8] mr-3"><ShieldCheck size={11} /> Verify</Link>
                              <a href={`${API}/funding/drafts/${d.verify_token}/certificate.pdf`} target="_blank" rel="noreferrer" data-testid={`frg-cert-${d.draft_no}`} className="inline-flex items-center gap-1 text-[10px] text-[#9DB4E8] hover:text-[#c3d3f5] mr-3"><Download size={11} /> Draft</a>
                              <a href={`${API}/funding/drafts/${d.verify_token}/ucc1.pdf`} target="_blank" rel="noreferrer" data-testid={`frg-ucc1-${d.draft_no}`} className="inline-flex items-center gap-1 text-[10px] text-[#9DB4E8] hover:text-[#c3d3f5]"><Download size={11} /> UCC-1</a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
